import type { PoLine } from '@/services/procurement'
import { displayCell } from '@/lib/cellDisplay'
import { legacyCellValue, legacyColumnNames } from '@/lib/legacySpecCells'

export type PoSheetDisplayLine = Pick<PoLine, 'spec' | 'spec_attributes' | 'priceSnapshot'> & {
  thick?: number | null
  width?: number | null
  length?: number | null
  height?: number | null
}

export type PoSheetColumn = { name: string; locked: boolean }

function sheetOf(line: PoSheetDisplayLine): NonNullable<NonNullable<PoLine['priceSnapshot']>['sheet']> | null {
  return line.priceSnapshot?.sheet ?? null
}

/** Merge PO columns from every sheet quote row, retaining first-seen order and lock state. */
export function poSheetColumnsOf(lines: PoSheetDisplayLine[]): PoSheetColumn[] {
  const result: PoSheetColumn[] = []
  const indexes = new Map<string, number>()
  for (const line of lines) {
    const columns = sheetOf(line)?.po_columns ?? []
    for (const column of columns) {
      const name = String(column.name ?? '').trim()
      if (!name) continue
      const existing = indexes.get(name)
      if (existing === undefined) {
        indexes.set(name, result.length)
        result.push({ name, locked: column.locked === true })
      } else if (column.locked === true) {
        result[existing] = { ...result[existing], locked: true }
      }
    }
  }
  const legacyNames = legacyColumnNames(lines.filter((line) => !sheetOf(line)).map((line) => ({
    spec: line.spec_attributes?.spec ?? line.spec,
    density: line.spec_attributes?.density,
  })))
  for (const name of legacyNames) {
    if (!indexes.has(name)) {
      indexes.set(name, result.length)
      result.push({ name, locked: false })
    }
  }
  return result
}

/** Read a quote column from its captured mapping/input; legacy PO rows keep legacy extraction. */
export function poSheetColumnValue(line: PoSheetDisplayLine, name: string): string {
  const sheet = sheetOf(line)
  if (sheet) {
    if (!(sheet.po_columns ?? []).some((column) => column.name === name)) return ''
    return displayCell(sheet.inputs?.[name] ?? sheet.mappings?.[name])
  }
  return displayCell(legacyCellValue({ ...(line.spec_attributes ?? {}), spec: line.spec_attributes?.spec ?? line.spec }, name))
}
